import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Menu, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { products, suiteBrands } from '@/data/products'
import { PATHS } from '@/app/router/routes'
import { useReducedMotion } from '@/components/motion'

const companyLinks = [
  { to: PATHS.about, label: 'Why' },
  { to: PATHS.blog, label: 'Blog' },
  { to: PATHS.about, label: 'About' },
  { to: PATHS.careers, label: 'Careers' },
]

export function Nav() {
  const prefersReducedMotion = useReducedMotion()
  const [railHover, setRailHover] = useState<string | null>(null)
  const [railFocus, setRailFocus] = useState<string | null>(null)
  const [condensed, setCondensed] = useState(false)

  useEffect(() => {
    document.documentElement.style.setProperty('--header-current-height', condensed ? '58px' : '')
    return () => { document.documentElement.style.removeProperty('--header-current-height') }
  }, [condensed])
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [isProductsOpen, setIsProductsOpen] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false)

  const productsRef = useRef<HTMLDivElement>(null)
  const productsTriggerRef = useRef<HTMLButtonElement>(null)
  const firstProductItemRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 8)
      setCondensed(window.scrollY > 80)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileOpen(false)
    setIsMobileProductsOpen(false)
    setIsProductsOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileOpen])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!productsRef.current) return
      if (!productsRef.current.contains(event.target as Node) && isProductsOpen) {
        setIsProductsOpen(false)
        productsTriggerRef.current?.focus()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (isProductsOpen) {
        setIsProductsOpen(false)
        productsTriggerRef.current?.focus()
      }
      if (isMobileOpen) {
        setIsMobileOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isProductsOpen, isMobileOpen])

  const openProductsWithKeyboard = () => {
    setIsProductsOpen(true)
    window.requestAnimationFrame(() => {
      firstProductItemRef.current?.focus()
    })
  }

  return (
    <header className={`nav-root ${scrolled ? 'nav-root--scrolled' : ''} ${condensed ? 'nav-root--condensed' : ''}`}>
      <div className="suite-rail">
        <div className="landing-container suite-rail-shell">
          <nav className="suite-rail-nav" aria-label="Returned product suite">
            {suiteBrands.map((brand) => {
              const className = `suite-rail-link ${brand.isCurrent ? 'suite-rail-link--current' : ''}`
              if (brand.isExternal) {
                return (
                  <a
                    key={brand.id}
                    href={brand.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                    onMouseEnter={() => setRailHover(brand.id)}
                    onMouseLeave={() => setRailHover(null)}
                    onFocus={() => setRailFocus(brand.id)}
                    onBlur={() => setRailFocus(null)}
                  >
                    <span className="suite-rail-label">
                      {brand.label}
                      {((railHover ?? railFocus) ? (railHover ?? railFocus) === brand.id : brand.isCurrent) && (
                        prefersReducedMotion
                          ? <span className="suite-rail-underline" />
                          : <motion.span layoutId="rail-underline" className="suite-rail-underline" transition={{ type: 'spring', stiffness: 500, damping: 40 }} />
                      )}
                    </span>
                  </a>
                )
              }

              return (
                <Link
                  key={brand.id}
                  to={PATHS.home}
                  className={className}
                  onMouseEnter={() => setRailHover(brand.id)}
                  onMouseLeave={() => setRailHover(null)}
                  onFocus={() => setRailFocus(brand.id)}
                  onBlur={() => setRailFocus(null)}
                >
                  <span className="suite-rail-label">
                    {brand.label}
                    {((railHover ?? railFocus) ? (railHover ?? railFocus) === brand.id : brand.isCurrent) && (
                      prefersReducedMotion
                        ? <span className="suite-rail-underline" />
                        : <motion.span layoutId="rail-underline" className="suite-rail-underline" transition={{ type: 'spring', stiffness: 500, damping: 40 }} />
                    )}
                  </span>
                </Link>
              )
            })}
          </nav>
          <p className="suite-rail-tagline">AI products that deepen human connection.</p>
        </div>
      </div>

      <div className="primary-nav">
        <div className="landing-container nav-shell">
          <Link to={PATHS.home} className="nav-brand-link" aria-label="Returned home">
            <img src="/logo-gradient.svg" alt="Returned" className="nav-logo" />
            <span className="nav-brand-text">Returned.com</span>
          </Link>

          <nav className="nav-desktop" aria-label="Main navigation">
            <div
              ref={productsRef}
              className="nav-products"
              onMouseEnter={() => setIsProductsOpen(true)}
              onMouseLeave={() => setIsProductsOpen(false)}
            >
              <button
                ref={productsTriggerRef}
                type="button"
                className="nav-link nav-button"
                aria-haspopup="menu"
                aria-expanded={isProductsOpen}
                onClick={() => setIsProductsOpen((current) => !current)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    if (isProductsOpen) {
                      setIsProductsOpen(false)
                      productsTriggerRef.current?.focus()
                    } else {
                      openProductsWithKeyboard()
                    }
                  }

                  if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    openProductsWithKeyboard()
                  }
                }}
              >
                Products <ChevronDown size={16} className={`nav-chevron ${isProductsOpen ? 'open' : ''}`} />
              </button>

              <AnimatePresence>
                {isProductsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    className="nav-dropdown"
                    role="menu"
                    aria-label="Products"
                  >
                    {products.map((product, index) => {
                      const Icon = product.icon
                      return (
                        <a
                          key={product.id}
                          ref={index === 0 ? firstProductItemRef : undefined}
                          href={product.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="nav-dropdown-item"
                          role="menuitem"
                          onClick={() => setIsProductsOpen(false)}
                        >
                          <span className="nav-dropdown-icon" style={{ background: `${product.color}20`, color: product.color }}>
                            <Icon size={16} />
                          </span>
                          <span>
                            <span className="nav-dropdown-name">{product.name}</span>
                            <span className="nav-dropdown-tagline">{product.tagline}</span>
                          </span>
                        </a>
                      )
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {companyLinks.map((link) => (
              <Link key={`${link.to}-${link.label}`} to={link.to} className="nav-link">
                {link.label}
              </Link>
            ))}

            <a href="https://helloreeva.com" target="_blank" rel="noopener noreferrer" className="nav-primary-cta">
              Join the Reeva beta
            </a>
          </nav>

          <button
            type="button"
            className="nav-mobile-toggle"
            aria-label={isMobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileOpen}
            onClick={() => setIsMobileOpen((open) => !open)}
          >
            {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="mobile-nav-overlay"
          >
            <div className="mobile-nav-content">
              <div className="mobile-suite-links" aria-label="Product suite">
                {suiteBrands.map((brand) => (
                  <a
                    key={brand.id}
                    href={brand.url}
                    target={brand.isExternal ? '_blank' : undefined}
                    rel={brand.isExternal ? 'noopener noreferrer' : undefined}
                    className={`mobile-suite-link ${brand.isCurrent ? 'mobile-suite-link--current' : ''}`}
                    onClick={() => setIsMobileOpen(false)}
                  >
                    {brand.label}
                  </a>
                ))}
              </div>

              <button
                type="button"
                className="mobile-nav-products-toggle"
                aria-haspopup="menu"
                aria-expanded={isMobileProductsOpen}
                onClick={() => setIsMobileProductsOpen((open) => !open)}
              >
                Products
                <ChevronDown size={16} className={`nav-chevron ${isMobileProductsOpen ? 'open' : ''}`} />
              </button>

              <AnimatePresence>
                {isMobileProductsOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mobile-nav-products-list"
                    role="menu"
                    aria-label="Products"
                  >
                    {products.map((product) => {
                      const Icon = product.icon
                      return (
                        <a
                          key={product.id}
                          href={product.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mobile-nav-product-link"
                          role="menuitem"
                          onClick={() => setIsMobileOpen(false)}
                        >
                          <span className="mobile-nav-product-icon" style={{ background: `${product.color}20`, color: product.color }}>
                            <Icon size={16} />
                          </span>
                          <span>
                            <span className="mobile-nav-product-name">{product.name}</span>
                            <span className="mobile-nav-product-tagline">{product.tagline}</span>
                          </span>
                        </a>
                      )
                    })}
                  </motion.div>
                )}
              </AnimatePresence>

              <Link to={PATHS.about} className="mobile-nav-link">Why we built this</Link>
              <Link to={PATHS.blog} className="mobile-nav-link">Blog</Link>
              <Link to={PATHS.about} className="mobile-nav-link">About</Link>
              <Link to={PATHS.careers} className="mobile-nav-link">Careers</Link>

              <a href="https://helloreeva.com" target="_blank" rel="noopener noreferrer" className="mobile-nav-primary-cta">
                Join the Reeva beta
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
